import styled from '@emotion/styled';
import Icon from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { message, Popover, Tabs, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { getTemplateList } from 'views/Aide/Whatsapp/service';
import { regionLangMap, regionPhoneMap } from 'views/Aide/Whatsapp/share';
import { ReactComponent as IconWhatsApp } from '../../Whatsapp/icon.svg';
import { WhatsAppTemplateListComponent } from '../WhatsAppTemplateList';

const allTab = '@@@ALL';

function WhatsAppTemplate({
  canSent,
  conversation,
  onSend,
}: {
  canSent: boolean;
  conversation: Conversation.Conversation;
  onSend?: () => void;
}) {
  const { t } = useTranslate();

  const [visible, setVisible] = useState(false);

  const { data } = useRequest(() => getTemplateList().then(({ details }) => details), {
    onError: () => message.error(t('common.feedback.fetchFailed')),
    ready: visible,
  });

  const { externalBuyerId } = conversation;

  const { groupTemplateListMap, groupList } = useMemo(() => {
    const targetLang =
      regionLangMap[
        Object.keys(regionPhoneMap).find((region) => {
          return externalBuyerId.startsWith(`${regionPhoneMap[region]}`);
        }) || 'SG'
      ];
    const groupTemplateListMap = data?.reduce<Record<string, any>>(
      (res, { name, template, show, status, group }) => {
        if (!show || !status) {
          return res;
        }
        return template.reduce((res, template) => {
          const { lang, state } = template;
          if (lang !== targetLang || state !== 'APPROVED') {
            return res;
          }
          const target = {
            ...template,
            name,
          };
          if (res[group]) {
            res[group].push(target);
          } else {
            res[group] = [target];
          }
          res[allTab].push(target);
          return res;
        }, res);
      },
      {
        [allTab]: [],
      },
    ) || {
      [allTab]: [],
    };
    const groupList = Object.keys(groupTemplateListMap);
    return {
      groupTemplateListMap,
      groupList,
    };
  }, [data, externalBuyerId]);

  const [tab, setTab] = useState<string>(allTab);

  const templateList = tab ? groupTemplateListMap[tab] || [] : [];

  return (
    <Popover
      visible={visible}
      content={
        <Container>
          <Tabs activeKey={tab} onChange={setTab}>
            {groupList.map((group) => {
              return (
                <Tabs.TabPane key={group} tab={group === allTab ? t('common.filter.all') : group} />
              );
            })}
          </Tabs>
          <WhatsAppTemplateListComponent
            templateList={templateList}
            conversation={conversation}
            onSend={onSend}
            onSendClick={() => setVisible(false)}
          />
        </Container>
      }
      trigger="click"
      onVisibleChange={setVisible}
    >
      <Tooltip title={t('scene.chatWorkspace.whatsAppTemplate.title')}>
        <Trigger component={IconWhatsApp} className={canSent ? 'canSent' : ''} />
      </Tooltip>
    </Popover>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 480px;
  height: 400px;
  margin: -12px -16px;
  > .ant-tabs {
    flex: 0 0 auto;
    .ant-tabs-nav {
      margin-bottom: 0;
      padding: 0 12px;
    }
  }
  > ul {
    flex: 1 0 0;
    overflow: auto;
  }
`;

const Trigger = styled(Icon)`
  width: 20px;
  height: 20px;
  font-size: 20px;
  &.canSent {
    position: relative;
    z-index: 2;
  }
`;

export default WhatsAppTemplate;
